"use client";

import Image from "next/image";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import styles from "./SiteHeader.module.css";
import { useTrackEvent } from "@/hooks/useTrackEvent";

const navigationLinks = [
  { label: "Home", href: "/" },
  { label: "Local Lessons", href: "/local-lessons" },
  { label: "About Liuba", href: "/about-liuba" },
  { label: "Success stories", href: "/success-stories" },
  { label: "Singing Tips", href: "/blog" },
  { label: "Contact", href: "/contact" },
];

const studioLinks = [
  { label: "Ely", href: "/singing-lessons-ely" },
  { label: "Bury St Edmunds", href: "/singing-lessons-bury-st-edmunds" },
];

const BRAND_LOGO_SRC = "/images/sa-com-pics/SA_LOGO-transparent.png";
const LOCAL_LESSONS_CTA = { label: "Book local lessons", href: "/local-lessons#buy" };
const SHOULD_PREFETCH_HEADER_LINKS = false;

function trackHeaderNavClick(
  trackEvent: ReturnType<typeof useTrackEvent>,
  href: string,
  label: string,
) {
  trackEvent("header_nav_click", {
    href,
    label,
    source_section: "site_header",
  });
}

function isActivePath(pathname: string, href: string) {
  return pathname === href || (href !== "/" && pathname.startsWith(`${href}/`));
}

export function SiteHeaderClient() {
  const pathname = usePathname();
  const [scrolled, setScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const trackEvent = useTrackEvent();
  const lessonsMenu = useRef<HTMLDetailsElement>(null);
  const mobileToggle = useRef<HTMLButtonElement>(null);

  const closeMenus = () => {
    setIsMobileOpen(false);
    if (lessonsMenu.current) lessonsMenu.current.open = false;
  };

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 50);
    handler();
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape" && lessonsMenu.current?.open) {
        lessonsMenu.current.open = false;
        lessonsMenu.current.querySelector("summary")?.focus();
      }
      if (event.key === "Escape" && isMobileOpen) {
        closeMenus();
        mobileToggle.current?.focus();
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isMobileOpen]);

  return (
    <header
      className={`sticky top-0 z-30 border-b transition-[background-color,box-shadow,backdrop-filter] duration-300 ${
        scrolled
          ? "border-[#ddd5c5]/80 bg-[#fffaf2]/96 shadow-[0_18px_60px_rgba(46,61,51,0.1)] backdrop-blur-xl dark:border-white/10 dark:bg-[#13211f]/94 dark:shadow-[0_22px_80px_rgba(0,0,0,0.34)]"
          : "border-[#ddd5c5]/70 bg-[#fffaf2]/94 backdrop-blur-xl dark:border-white/10 dark:bg-[#13211f]/90"
      }`}
    >
      <div className="border-b border-[#d9a93d]/20 bg-[#123b34] text-[#fffaf2]">
        <div className="mx-auto flex min-h-8 max-w-[92rem] items-center justify-between gap-3 px-4 py-1.5 text-[10px] font-medium sm:px-6 sm:text-xs lg:px-8">
          <span className="tracking-[0.08em]">Technique · Attitude · Expression</span>
          <Link
            href="https://singingattitude.com"
            target="_blank"
            rel="noreferrer"
            aria-label="Online coaching at SingingAttitude.com (opens in a new tab)"
            className="inline-flex shrink-0 items-center gap-1.5 text-[#f0d58c] transition hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#f0d58c]"
            onClick={() => trackHeaderNavClick(trackEvent, "https://singingattitude.com", "Online coaching")}
          >
            <span aria-hidden="true">◎</span> Online coaching <span className="hidden sm:inline">at <strong>SingingAttitude.com</strong></span> <span aria-hidden="true">↗</span>
          </Link>
        </div>
      </div>
      <div className="mx-auto w-full max-w-[92rem] px-4 sm:px-6 lg:px-8">
        <div className="grid min-h-[80px] grid-cols-[minmax(0,1fr)_auto] items-center gap-3 py-2 sm:min-h-[90px] sm:gap-4">
          <div className="min-w-0">
            <Link
              href="/"
              prefetch={SHOULD_PREFETCH_HEADER_LINKS}
              className={`${styles.brand} inline-flex min-w-0 items-center gap-2.5 rounded-[14px] sm:gap-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#35716a]`}
              onClick={closeMenus}
              aria-label="Singing Attitude UK home"
            >
              <Image
                src={BRAND_LOGO_SRC}
                alt="Singing Attitude logo"
                width={736}
                height={844}
                className="h-auto w-14 shrink-0 sm:w-16"
                sizes="(min-width: 640px) 64px, 56px"
              />
              <span className="min-w-0">
                <span className={`${styles.wordmark} block whitespace-nowrap font-serif text-[1.05rem] font-medium leading-none text-[#13211f] dark:text-[#f6fbf8] sm:text-2xl md:text-[1.7rem]`}>
                  Singing Attitude <span className={`${styles.country} text-[#9a6a18] dark:text-[#e7c574]`}>UK</span>
                </span>
                <svg className={styles.flourish} viewBox="0 0 112 15" aria-hidden="true"><path d="M2 12C32-10 64 26 109 5C75 31 41-3 2 12Z" fill="currentColor" /></svg>
              </span>
            </Link>
          </div>

          <div className="flex items-center justify-end gap-2 sm:gap-3">
            <nav aria-label="Primary navigation" className="hidden items-center gap-0.5 xl:flex">
              <details ref={lessonsMenu} className={styles.lessonsMenu}>
                <summary className={styles.lessonsToggle}>Lessons <span aria-hidden="true">⌄</span></summary>
                <div className={styles.lessonsPanel}>
                  {[navigationLinks[1], ...studioLinks].map((link) => (
                    <Link key={link.href} href={link.href} prefetch={SHOULD_PREFETCH_HEADER_LINKS} aria-current={isActivePath(pathname, link.href) ? "page" : undefined} onClick={() => { closeMenus(); trackHeaderNavClick(trackEvent, link.href, link.label); }}>{link.label}</Link>
                  ))}
                </div>
              </details>
              {navigationLinks.slice(2).map((link) => {
                const isActive = isActivePath(pathname, link.href);
                return (
                  <Link
                    key={link.href}
                    href={link.href}
                    prefetch={SHOULD_PREFETCH_HEADER_LINKS}
                    aria-current={isActive ? "page" : undefined}
                    className={`rounded-lg px-3 py-3 text-[0.8rem] font-semibold transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#35716a] ${
                      isActive
                        ? "bg-[#eee4d0] text-[#123b34] shadow-[inset_0_-2px_0_#d9a93d] dark:bg-white/10 dark:text-[#f0d58c]"
                        : "text-[#5d5448] hover:bg-[#fffaf2] hover:text-[#13211f] dark:text-[#dbe7e2]/76 dark:hover:bg-white/[0.08] dark:hover:text-[#fffaf2]"
                    }`}
                    onClick={() => {
                      closeMenus();
                      trackHeaderNavClick(trackEvent, link.href, link.label);
                    }}
                  >
                    {link.label}
                  </Link>
                );
              })}
            </nav>

            <Link
              href={LOCAL_LESSONS_CTA.href}
              prefetch={SHOULD_PREFETCH_HEADER_LINKS}
              className="btn-premium hidden min-h-10 items-center justify-center whitespace-nowrap rounded-xl bg-[#d9a93d] px-5 text-sm font-bold text-[#123b34] shadow-[0_6px_18px_rgba(166,144,91,0.2)] transition hover:-translate-y-0.5 hover:bg-[#e4b955] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#35716a] sm:inline-flex"
              onClick={() => {
                closeMenus();
                trackHeaderNavClick(trackEvent, LOCAL_LESSONS_CTA.href, LOCAL_LESSONS_CTA.label);
              }}
            >
              {LOCAL_LESSONS_CTA.label} <span aria-hidden="true" className="ml-3">→</span>
            </Link>

            <button
              ref={mobileToggle}
              type="button"
              className="min-h-11 rounded-full border border-[#ddd5c5] bg-[#fffaf2]/70 px-3 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-[#5d5448] transition hover:border-[#35716a]/45 hover:text-[#13211f] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#35716a] dark:border-white/14 dark:bg-white/[0.03] dark:text-[#dbe7e2] dark:hover:text-[#f6fbf8] xl:hidden"
              onClick={() => setIsMobileOpen((prev) => !prev)}
              aria-label={isMobileOpen ? "Close" : "Menu"}
              aria-expanded={isMobileOpen}
              aria-controls="mobile-menu"
            >
              {isMobileOpen ? "Close" : "Menu"}
            </button>
          </div>
        </div>

        {isMobileOpen ? (
          <div
            id="mobile-menu"
            className="mb-4 xl:hidden rounded-[24px] border border-[#ddd5c5] bg-[#fffaf2]/96 p-5 text-sm text-[#5d5448] shadow-[0_20px_60px_rgba(46,61,51,0.16)] dark:border-white/10 dark:bg-[#13211f]/96 dark:text-[#dbe7e2]/80"
          >
            <div className="mb-5">
              <p className="font-serif text-2xl font-semibold text-[#13211f] dark:text-[#f6fbf8]">Singing Attitude UK</p>
              <p className="mt-1 text-xs font-semibold uppercase tracking-[0.18em] text-[#5f553c] dark:text-[#dbe7e2]/66">
                Ely & Bury local lessons
              </p>
            </div>

            <nav aria-label="Mobile navigation" className="grid gap-1 sm:grid-cols-2">
              {[...navigationLinks.slice(0, 2), ...studioLinks, ...navigationLinks.slice(2)].map((link) => {
                const isActive = isActivePath(pathname, link.href);
                return (
                  <Link
                    key={link.href}
                    href={link.href}
                    prefetch={SHOULD_PREFETCH_HEADER_LINKS}
                    aria-current={isActive ? "page" : undefined}
                    className="flex min-h-11 items-center justify-between rounded-lg border-b border-[#ddd5c5]/60 px-2 text-base font-semibold text-[#13211f] transition hover:text-[#35716a] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#35716a] dark:text-[#f6fbf8] dark:hover:text-[#d9c78f]"
                    onClick={() => {
                      closeMenus();
                      trackHeaderNavClick(trackEvent, link.href, link.label);
                    }}
                  >
                    <span>{link.label}</span>
                    {isActive ? <span className="h-1.5 w-1.5 rounded-full bg-[#d9c78f]" aria-hidden /> : null}
                  </Link>
                );
              })}
            </nav>

            <div className="mt-6 grid gap-3">
              <Link
                href={LOCAL_LESSONS_CTA.href}
                prefetch={SHOULD_PREFETCH_HEADER_LINKS}
                className="btn-premium inline-flex min-h-12 items-center justify-center rounded-xl bg-[#d9a93d] text-sm font-bold text-[#123b34] transition hover:bg-[#e4b955] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#35716a]"
                onClick={() => {
                  closeMenus();
                  trackHeaderNavClick(trackEvent, LOCAL_LESSONS_CTA.href, LOCAL_LESSONS_CTA.label);
                }}
              >
                {LOCAL_LESSONS_CTA.label}
              </Link>
            </div>
          </div>
        ) : null}
      </div>
    </header>
  );
}
